import React, { memo } from 'react';
import { Icons } from '../Icons';
import { useLanguage } from '../../contexts/LanguageContext';

const LabTestTip = memo(({ toolName, toolNameEn, targetTab, currentUrl }) => {
  const { language } = useLanguage();
  let href = '/lab-insights';
  if (currentUrl && targetTab) {
    try {
      const cleaned = currentUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
      href = `/?origin=${encodeURIComponent(cleaned)}&tab=lab&subTab=${encodeURIComponent(targetTab)}`;
    } catch (_) {}
  }
  return (
    <a 
      href={href}
      className="mt-3 p-2 bg-purple-500/10 border border-purple-500/20 rounded-lg flex items-center gap-2 hover:bg-purple-500/20 hover:border-purple-500/30 transition-colors cursor-pointer group no-underline"
    >
      <Icons.Lightning size={12} className="text-purple-400 shrink-0" />
      <span className="text-[10px] text-purple-300 flex-1">
        {language === 'cs' 
          ? `Spustit „${toolName}"`
          : `Run "${toolNameEn}"`}
      </span>
      <Icons.ArrowRight size={12} className="text-purple-400 group-hover:translate-x-0.5 transition-transform" />
    </a>
  );
});

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const truncateUrl = (url) => {
  if (!url) return '';
  return url;
};

const LayoutShiftAttribution = memo(({ data }) => {
  const { language } = useLanguage();
  
  if (!data || data.elements?.length === 0) return null;
  
  const getScoreColor = (score) => {
    if (score < 0.05) return 'text-green-400';
    if (score < 0.1) return 'text-yellow-400';
    return 'text-red-400';
  };
  
  return (
    <div className="p-4 rounded-lg bg-gradient-to-r from-orange-900/30 to-red-900/30 border border-orange-500/30">
      <div className="text-xs uppercase tracking-wider text-orange-400 mb-3 flex items-center gap-2">
        <Icons.Layers size={14} />
        {language === 'cs' ? 'Layout Shift Attribution' : 'Layout Shift Attribution'}
      </div>
      
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center p-2 bg-black/20 rounded">
          <div className={`text-lg font-bold ${getScoreColor(data.totalCLS)}`}>
            {data.totalCLS?.toFixed(3) || '0'}
          </div>
          <div className="text-xs text-gray-400">Total CLS</div>
        </div>
        <div className="text-center p-2 bg-black/20 rounded">
          <div className="text-lg font-bold text-orange-400">{data.shiftCount || 0}</div>
          <div className="text-xs text-gray-400">{language === 'cs' ? 'Posunů' : 'Shifts'}</div>
        </div>
        <div className="text-center p-2 bg-black/20 rounded">
          <div className={`text-lg font-bold ${getScoreColor(data.largestShift)}`}>
            {data.largestShift?.toFixed(3) || '0'}
          </div>
          <div className="text-xs text-gray-400">{language === 'cs' ? 'Největší' : 'Largest'}</div>
        </div>
      </div>
      
      {data.elements?.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs text-gray-400 mb-2">
            {language === 'cs' ? 'Elementy způsobující posuny:' : 'Elements causing shifts:'}
          </div>
          {data.elements.slice(0, 5).map((el, i) => (
            <div key={i} className="flex items-center justify-between p-2 bg-black/20 rounded text-xs">
              <div className="flex-1 min-w-0">
                <div className="text-gray-300 break-all font-mono text-[10px]">
                  {el.snippet || el.selector || el.nodeLabel || 'Unknown element'}
                </div>
              </div>
              <div className={`ml-2 font-bold ${getScoreColor(el.score)}`}>
                {el.score?.toFixed(4) || '0'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

const CriticalRenderingPath = memo(({ data }) => {
  const { language } = useLanguage();
  
  if (!data || data.renderBlockingResources?.length === 0) return null;
  
  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'css': return '🎨';
      case 'js': return '⚡';
      case 'woff2':
      case 'woff':
      case 'ttf': return '🔤';
      default: return '📄';
    }
  };
  
  return (
    <div className="p-4 rounded-lg bg-gradient-to-r from-red-900/30 to-pink-900/30 border border-red-500/30">
      <div className="text-xs uppercase tracking-wider text-red-400 mb-3 flex items-center gap-2">
        <Icons.Zap size={14} />
        {language === 'cs' ? 'Critical Rendering Path' : 'Critical Rendering Path'}
      </div>
      
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center p-2 bg-black/20 rounded">
          <div className="text-lg font-bold text-red-400">
            {Math.round(data.totalBlockingTime)} ms
          </div>
          <div className="text-xs text-gray-400">{language === 'cs' ? 'Blokující čas' : 'Blocking Time'}</div>
        </div>
        <div className="text-center p-2 bg-black/20 rounded">
          <div className="text-lg font-bold text-orange-400">
            {formatBytes(data.totalBlockingBytes)}
          </div>
          <div className="text-xs text-gray-400">{language === 'cs' ? 'Blokující data' : 'Blocking Bytes'}</div>
        </div>
        <div className="text-center p-2 bg-black/20 rounded">
          <div className="text-lg font-bold text-yellow-400">{data.criticalChainDepth || 0}</div>
          <div className="text-xs text-gray-400">{language === 'cs' ? 'Hloubka řetězce' : 'Chain Depth'}</div>
        </div>
      </div>
      
      <div className="space-y-1">
        <div className="text-xs text-gray-400 mb-2">
          {language === 'cs' ? 'Render-blocking zdroje:' : 'Render-blocking resources:'}
        </div>
        {data.renderBlockingResources.slice(0, 6).map((r, i) => (
          <div key={i} className="flex items-center gap-2 p-2 bg-black/20 rounded text-xs">
            <span>{getTypeIcon(r.type)}</span>
            <div className="flex-1 min-w-0 text-gray-300 break-all text-[10px] leading-tight" title={r.url}>
              {r.url}
            </div>
            <div className="text-red-400 whitespace-nowrap">
              +{Math.round(r.wastedMs)} ms
            </div>
            <div className="text-gray-500 whitespace-nowrap">
              {formatBytes(r.totalBytes)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

const FontLoadingAnalysis = memo(({ data, currentUrl }) => {
  const { language } = useLanguage();
  
  if (!data || data.totalFonts === 0) return null;
  
  const getTips = () => {
    const tips = [];
    if (language === 'cs') {
      if (data.fontsWithoutSwap?.length > 0) tips.push('Přidejte font-display: swap do @font-face - zabrání neviditelnému textu při načítání');
      if (data.foitRisk) tips.push('FOIT riziko (Flash of Invisible Text) - text je neviditelný během načítání fontu, což frustruje uživatele');
      if (data.foutRisk) tips.push('FOUT (Flash of Unstyled Text) - je přijatelnější než FOIT, text je čitelný, jen se změní vzhled');
      if (data.totalFonts > 3 && (data.fontsWithoutSwap?.length > 0 || data.foitRisk)) tips.push(`Používáte ${data.totalFonts} fontů - zvažte omezení na 2-3 pro rychlejší načtení`);
      if (!tips.length) tips.push('Fonty jsou správně nakonfigurovány - font-display: swap zajišťuje viditelný text');
    } else {
      if (data.fontsWithoutSwap?.length > 0) tips.push('Add font-display: swap to @font-face - prevents invisible text during loading');
      if (data.foitRisk) tips.push('FOIT risk (Flash of Invisible Text) - text is invisible during font loading, frustrating users');
      if (data.foutRisk) tips.push('FOUT (Flash of Unstyled Text) - more acceptable than FOIT, text is readable but changes appearance');
      if (data.totalFonts > 3 && (data.fontsWithoutSwap?.length > 0 || data.foitRisk)) tips.push(`Using ${data.totalFonts} fonts - consider limiting to 2-3 for faster loading`);
      if (!tips.length) tips.push('Fonts are properly configured - font-display: swap ensures visible text');
    }
    return tips;
  };
  
  const tips = getTips();
  
  return (
    <div className="p-4 rounded-lg bg-gradient-to-r from-indigo-900/30 to-purple-900/30 border border-indigo-500/30">
      <div className="text-xs uppercase tracking-wider text-indigo-400 mb-2 flex items-center gap-2">
        <Icons.Type size={14} />
        {language === 'cs' ? 'Font Loading Analysis' : 'Font Loading Analysis'}
      </div>
      <p className="text-xs text-gray-400 mb-3">
        {language === 'cs' 
          ? 'Analýza načítání fontů. FOIT = neviditelný text, FOUT = změna stylu textu po načtení fontu.' 
          : 'Font loading analysis. FOIT = invisible text, FOUT = text style change after font loads.'}
      </p>
      
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="text-center p-2 bg-black/20 rounded">
          <div className="text-lg font-bold text-indigo-400">{data.totalFonts}</div>
          <div className="text-xs text-gray-400">{language === 'cs' ? 'Fontů' : 'Fonts'}</div>
        </div>
        <div className="text-center p-2 bg-black/20 rounded">
          <div className="text-lg font-bold text-yellow-400">{data.fontsWithoutSwap?.length || 0}</div>
          <div className="text-xs text-gray-400">{language === 'cs' ? 'Bez swap' : 'No swap'}</div>
        </div>
        <div className="text-center p-2 bg-black/20 rounded">
          <div className={`text-lg font-bold ${data.foitRisk ? 'text-red-400' : 'text-green-400'}`}>
            {data.foitRisk ? '⚠️' : '✓'}
          </div>
          <div className="text-xs text-gray-400">FOIT</div>
        </div>
        <div className="text-center p-2 bg-black/20 rounded">
          <div className={`text-lg font-bold ${data.foutRisk ? 'text-yellow-400' : 'text-green-400'}`}>
            {data.foutRisk ? '⚠️' : '✓'}
          </div>
          <div className="text-xs text-gray-400">FOUT</div>
        </div>
      </div>
      
      {data.fontsWithoutSwap?.length > 0 && (
        <div className="mb-3">
          <div className="text-xs text-yellow-400 mb-1">
            {language === 'cs' ? 'Fonty bez font-display: swap' : 'Fonts without font-display: swap'}
          </div>
          {data.fontsWithoutSwap.map((f, i) => (
            <div key={i} className="text-xs text-gray-400 break-all p-1 bg-black/20 rounded mb-1" title={f.url}>
              {f.url}
            </div>
          ))}
        </div>
      )}
      
      {data.fontDetails?.length > 0 && (
        <div className="space-y-1 mb-3">
          <div className="text-xs text-gray-400">
            {language === 'cs' ? 'Detaily fontů:' : 'Font details:'}
          </div>
          {data.fontDetails.slice(0, 4).map((f, i) => (
            <div key={i} className="flex items-center justify-between p-2 bg-black/20 rounded text-xs gap-2">
              <div className="flex-1 min-w-0 text-gray-300 break-all text-[10px] leading-tight" title={f.url}>
                {truncateUrl(f.url)}
              </div>
              <div className="flex gap-3 text-gray-400">
                <span>{formatBytes(f.size)}</span>
                <span>{(f.duration * 1000).toFixed(0)} ms</span>
              </div>
            </div>
          ))}
        </div>
      )}
      
      <div className="p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-lg">
        <div className="text-xs font-medium text-indigo-400 mb-2 flex items-center gap-1">
          <Icons.Lightbulb size={12} />
          {language === 'cs' ? 'Tip:' : 'Tip:'}
        </div>
        <p className="text-xs text-gray-300">{tips[0]}</p>
      </div>
      
      <LabTestTip toolName="Analýza třetích stran" toolNameEn="Third-Party Analysis" targetTab="thirdParty" currentUrl={currentUrl} />
    </div>
  );
});

const ImageOptimization = memo(({ data, currentUrl }) => {
  const { language } = useLanguage();
  
  if (!data || data.totalIssues === 0) return null;
  
  const getScoreColor = (score) => {
    if (score >= 0.9) return 'text-green-400';
    if (score >= 0.5) return 'text-yellow-400';
    return 'text-red-400';
  };
  
  const getTip = () => {
    if (language === 'cs') {
      if (data.unsizedImages?.length > 0) return 'Obrázky bez rozměrů způsobují CLS - přidejte width a height atributy do <img> tagů';
      if (data.needsModernFormat?.length > 0) return 'Konvertujte obrázky do WebP/AVIF - úspora 30-50% bez ztráty kvality';
      if (data.needsLazyLoading?.length > 0) return 'Přidejte loading="lazy" k obrázkům pod viditelnou částí stránky';
      return 'Optimalizujte obrázky pro rychlejší načítání a lepší uživatelský zážitek';
    } else {
      if (data.unsizedImages?.length > 0) return 'Images without dimensions cause CLS - add width and height attributes to <img> tags';
      if (data.needsModernFormat?.length > 0) return 'Convert images to WebP/AVIF - save 30-50% without quality loss';
      if (data.needsLazyLoading?.length > 0) return 'Add loading="lazy" to images below the fold';
      return 'Optimize images for faster loading and better user experience';
    }
  };
  
  return (
    <div className="p-4 rounded-lg bg-gradient-to-r from-emerald-900/30 to-teal-900/30 border border-emerald-500/30">
      <div className="text-xs uppercase tracking-wider text-emerald-400 mb-2 flex items-center gap-2">
        <Icons.Image size={14} />
        {language === 'cs' ? 'Image Optimization' : 'Image Optimization'}
      </div>
      <p className="text-xs text-gray-400 mb-3">
        {language === 'cs' 
          ? 'Obrázky jsou často největší částí stránky. Optimalizace zrychlí načítání a sníží datový objem.' 
          : 'Images are often the largest part of a page. Optimization speeds up loading and reduces data.'}
      </p>
      
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="text-center p-2 bg-black/20 rounded">
          <div className="text-lg font-bold text-red-400">{data.totalIssues}</div>
          <div className="text-xs text-gray-400">{language === 'cs' ? 'Problémů' : 'Issues'}</div>
        </div>
        <div className="text-center p-2 bg-black/20 rounded">
          <div className="text-lg font-bold text-orange-400">{formatBytes(data.totalWastedBytes)}</div>
          <div className="text-xs text-gray-400">{language === 'cs' ? 'Zbytečná data' : 'Wasted Bytes'}</div>
        </div>
      </div>
      
      <div className="grid grid-cols-4 gap-2 mb-4">
        {Object.entries(data.scores || {}).map(([key, score]) => (
          <div key={key} className="text-center p-1 bg-black/20 rounded">
            <div className={`text-sm font-bold ${getScoreColor(score)}`}>
              {score !== null ? Math.round((score || 0) * 100) : '?'}
            </div>
            <div className="text-[10px] text-gray-500 capitalize">{key}</div>
          </div>
        ))}
      </div>
      
      <div className="space-y-2 mb-3">
        {data.needsModernFormat?.length > 0 && (
          <div className="p-2 bg-black/20 rounded">
            <div className="text-xs text-yellow-400 mb-1">
              🖼️ {language === 'cs' ? 'Potřebuje WebP/AVIF' : 'Needs WebP/AVIF'} ({data.needsModernFormat.length})
            </div>
            <div className="text-xs text-gray-500">
              {language === 'cs' ? 'Možná úspora:' : 'Potential savings:'} {formatBytes(data.needsModernFormat.reduce((s, i) => s + (i.wastedBytes || 0), 0))}
            </div>
          </div>
        )}
        
        {data.needsLazyLoading?.length > 0 && (
          <div className="p-2 bg-black/20 rounded">
            <div className="text-xs text-blue-400 mb-1">
              ⏳ {language === 'cs' ? 'Potřebuje lazy loading' : 'Needs lazy loading'} ({data.needsLazyLoading.length})
            </div>
            <div className="text-xs text-gray-500">
              {language === 'cs' ? 'Odložitelná data:' : 'Deferrable:'} {formatBytes(data.needsLazyLoading.reduce((s, i) => s + (i.wastedBytes || 0), 0))}
            </div>
          </div>
        )}
        
        {data.unsizedImages?.length > 0 && (
          <div className="p-2 bg-black/20 rounded">
            <div className="text-xs text-red-400 mb-1">
              📐 {language === 'cs' ? 'Bez rozměrů (CLS riziko)' : 'Missing dimensions (CLS risk)'} ({data.unsizedImages.length})
            </div>
          </div>
        )}
      </div>
      
      <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
        <div className="text-xs font-medium text-emerald-400 mb-1 flex items-center gap-1">
          <Icons.Lightbulb size={12} />
          {language === 'cs' ? 'Doporučení:' : 'Recommendation:'}
        </div>
        <p className="text-xs text-gray-300">{getTip()}</p>
      </div>
      
      <LabTestTip toolName="Lazy Loading Audit" toolNameEn="Lazy Loading Audit" targetTab="lazyloading" currentUrl={currentUrl} />
    </div>
  );
});

const PerformanceBudget = memo(({ data, currentUrl }) => {
  const { language } = useLanguage();
  
  if (!data) return null;
  
  const typeLabels = {
    total: language === 'cs' ? 'Celkem' : 'Total',
    script: 'JavaScript',
    stylesheet: 'CSS',
    image: language === 'cs' ? 'Obrázky' : 'Images',
    font: language === 'cs' ? 'Fonty' : 'Fonts',
    document: 'HTML',
    other: language === 'cs' ? 'Ostatní' : 'Other'
  };
  
  const getBudgetColor = (percentage) => {
    if (percentage <= 70) return 'bg-green-500';
    if (percentage <= 100) return 'bg-yellow-500';
    return 'bg-red-500';
  };
  
  const overBudgetItems = data.budgetStatus?.filter(b => b.overBudget) || [];
  
  const getTips = () => {
    const tips = [];
    if (language === 'cs') {
      overBudgetItems.forEach(b => {
        if (b.type === 'script') tips.push('JavaScript přesahuje limit - rozdělte kód na menší části (code splitting), odstraňte nepoužívané knihovny');
        if (b.type === 'image') tips.push('Obrázky přesahují limit - použijte WebP/AVIF formát, lazy loading a responzivní velikosti');
        if (b.type === 'stylesheet') tips.push('CSS přesahuje limit - odstraňte nepoužité styly, zvažte critical CSS inline');
        if (b.type === 'font') tips.push('Fonty přesahují limit - omezte počet variant, použijte subset pouze pro potřebné znaky');
      });
      if (data.unusedJs?.wastedBytes > 50000) tips.push(`Nepoužitý JavaScript (${formatBytes(data.unusedJs.wastedBytes)}) - analyzujte bundle pomocí webpack-bundle-analyzer`);
      if (data.unusedCss?.wastedBytes > 10000) tips.push(`Nepoužité CSS (${formatBytes(data.unusedCss.wastedBytes)}) - použijte PurgeCSS nebo podobný nástroj`);
    } else {
      overBudgetItems.forEach(b => {
        if (b.type === 'script') tips.push('JavaScript over budget - implement code splitting, remove unused libraries');
        if (b.type === 'image') tips.push('Images over budget - use WebP/AVIF format, lazy loading, and responsive sizes');
        if (b.type === 'stylesheet') tips.push('CSS over budget - remove unused styles, consider critical CSS inline');
        if (b.type === 'font') tips.push('Fonts over budget - limit variants, use subset for required characters only');
      });
      if (data.unusedJs?.wastedBytes > 50000) tips.push(`Unused JavaScript (${formatBytes(data.unusedJs.wastedBytes)}) - analyze bundle with webpack-bundle-analyzer`);
      if (data.unusedCss?.wastedBytes > 10000) tips.push(`Unused CSS (${formatBytes(data.unusedCss.wastedBytes)}) - use PurgeCSS or similar tool`);
    }
    return tips;
  };
  
  const tips = getTips();
  
  return (
    <div className="p-4 rounded-lg bg-gradient-to-r from-blue-900/30 to-cyan-900/30 border border-blue-500/30">
      <div className="text-xs uppercase tracking-wider text-blue-400 mb-2 flex items-center gap-2">
        <Icons.PieChart size={14} />
        {language === 'cs' ? 'Performance Budget' : 'Performance Budget'}
      </div>
      <p className="text-xs text-gray-400 mb-3">
        {language === 'cs' 
          ? 'Performance budget definuje maximální přípustnou velikost zdrojů pro rychlé načtení. Překročení limitů zpomaluje web.' 
          : 'Performance budget defines maximum allowed resource sizes for fast loading. Exceeding limits slows down your site.'}
      </p>
      
      <div className="space-y-2 mb-4">
        {data.budgetStatus?.filter(b => b.current > 0).map((budget, i) => (
          <div key={i} className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-gray-300">{typeLabels[budget.type] || budget.type}</span>
              <span className={budget.overBudget ? 'text-red-400' : 'text-gray-400'}>
                {formatBytes(budget.current)} / {formatBytes(budget.limit)}
                {budget.overBudget && ` (+${formatBytes(budget.overage)})`}
              </span>
            </div>
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
              <div 
                className={`h-full ${getBudgetColor(budget.percentage)} transition-all`}
                style={{ width: `${Math.min(budget.percentage, 100)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
      
      <div className="grid grid-cols-3 gap-2 mb-3">
        {data.unusedCss?.wastedBytes > 0 && (
          <div className="text-center p-2 bg-black/20 rounded">
            <div className="text-sm font-bold text-yellow-400">
              {formatBytes(data.unusedCss.wastedBytes)}
            </div>
            <div className="text-[10px] text-gray-500">{language === 'cs' ? 'Nepoužité CSS' : 'Unused CSS'}</div>
          </div>
        )}
        {data.unusedJs?.wastedBytes > 0 && (
          <div className="text-center p-2 bg-black/20 rounded">
            <div className="text-sm font-bold text-orange-400">
              {formatBytes(data.unusedJs.wastedBytes)}
            </div>
            <div className="text-[10px] text-gray-500">{language === 'cs' ? 'Nepoužitý JS' : 'Unused JS'}</div>
          </div>
        )}
        {data.duplicateJs?.wastedBytes > 0 && (
          <div className="text-center p-2 bg-black/20 rounded">
            <div className="text-sm font-bold text-red-400">
              {formatBytes(data.duplicateJs.wastedBytes)}
            </div>
            <div className="text-[10px] text-gray-500">{language === 'cs' ? 'Duplicitní JS' : 'Duplicate JS'}</div>
          </div>
        )}
      </div>
      
      {tips.length > 0 && (
        <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <div className="text-xs font-medium text-blue-400 mb-2 flex items-center gap-1">
            <Icons.Lightbulb size={12} />
            {language === 'cs' ? 'Doporučení:' : 'Recommendations:'}
          </div>
          <ul className="text-xs text-gray-300 space-y-1">
            {tips.slice(0, 3).map((tip, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-blue-400 mt-0.5">•</span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      <LabTestTip toolName="Analýza pokrytí kódu" toolNameEn="Code Coverage Analysis" targetTab="coverage" currentUrl={currentUrl} />
    </div>
  );
});

const AdvancedDiagnostics = ({ diagnostics, currentUrl }) => {
  const { language } = useLanguage();
  
  if (!diagnostics) return null;
  
  const hasData = diagnostics.layoutShiftAttribution?.elements?.length > 0 ||
    diagnostics.criticalRenderingPath?.renderBlockingResources?.length > 0 ||
    diagnostics.fontLoading?.totalFonts > 0 ||
    diagnostics.imageOptimization?.totalIssues > 0 ||
    diagnostics.performanceBudget?.budgetStatus?.length > 0;
  
  if (!hasData) return null;
  
  return (
    <div className="mt-6">
      <h4 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
        <Icons.Activity size={16} className="text-cyan-400" />
        {language === 'cs' ? 'Pokročilá diagnostika' : 'Advanced Diagnostics'}
      </h4>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <LayoutShiftAttribution data={diagnostics.layoutShiftAttribution} />
        <CriticalRenderingPath data={diagnostics.criticalRenderingPath} />
        <FontLoadingAnalysis data={diagnostics.fontLoading} currentUrl={currentUrl} />
        <ImageOptimization data={diagnostics.imageOptimization} currentUrl={currentUrl} />
        <PerformanceBudget data={diagnostics.performanceBudget} currentUrl={currentUrl} />
      </div>
      
      <div className="mt-3 text-[10px] text-gray-500 text-center">
        {language === 'cs' 
          ? 'Data z Lighthouse auditu' 
          : 'Data from Lighthouse audit'}
      </div>
    </div>
  );
};

export default AdvancedDiagnostics;
